<template>
  <div id="app">
    <app-detail-header :title="'Bulk Assign ORCID Units'"></app-detail-header>
    <b-container>
      <b-row>
        <b-col lg="10" xl="8">
          <b-card title="Bulk Assign Units to ORCID IDs" class="mt-3" header-tag="header" variant="primary">
            <p class="text-muted mb-3">
              Enter ORCID IDs separated by commas or newlines (one per line). The same number of units will be assigned to each.
            </p>

            <b-form @submit.prevent="onSubmit">
              <b-form-group label="ORCID IDs" label-class="font-weight-bold">
                <b-form-textarea
                  v-model="orcidInput"
                  placeholder="e.g. 0000-0002-9997-6489, 0000-0001-2345-6789&#10;or one per line:&#10;0000-0002-9997-6489&#10;0000-0001-2345-6789"
                  rows="4"
                  trim
                ></b-form-textarea>
                <small class="text-muted">{{ parsedOrcids.length }} ORCID ID(s) detected</small>
              </b-form-group>

              <b-row>
                <b-col md="3">
                  <b-form-group label="Units" label-class="font-weight-bold">
                    <b-form-input
                      v-model.number="units"
                      type="number"
                      min="1"
                      placeholder="e.g. 300"
                      required
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Transaction Type" label-class="font-weight-bold">
                    <b-form-select
                      v-model="transactionType"
                      :options="transactionTypeOptions"
                      required
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="5">
                  <b-form-group label="Invoice / Request Info" label-class="font-weight-bold">
                    <b-form-input
                      v-model="invoiceInfo"
                      placeholder="e.g. Invoice 4417 paid"
                      required
                      trim
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-button
                type="submit"
                variant="primary"
                :disabled="submitting || parsedOrcids.length === 0 || !transactionType || !invoiceInfo.trim()"
              >
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                {{ submitting ? "Processing..." : `Assign Units (${parsedOrcids.length})` }}
              </b-button>
              <b-button
                type="button"
                variant="outline-secondary"
                class="ml-2"
                :disabled="submitting"
                @click="resetForm"
              >
                Reset
              </b-button>
            </b-form>

            <b-alert v-if="submitError" variant="danger" show class="mt-3 mb-0">
              <strong>Error:</strong> {{ submitError }}
            </b-alert>
          </b-card>

          <!-- Results -->
          <b-card v-if="bulkResult" title="Results" class="mt-3" header-tag="header">
            <b-row class="mb-3">
              <b-col md="4">
                <div class="text-center p-3 border rounded">
                  <div class="h3 font-weight-bold mb-0">{{ bulkResult.totalProcessed }}</div>
                  <div class="text-muted small">Total Processed</div>
                </div>
              </b-col>
              <b-col md="4">
                <div class="text-center p-3 border rounded bg-success text-white">
                  <div class="h3 font-weight-bold mb-0">{{ bulkResult.successCount }}</div>
                  <div class="small">Succeeded</div>
                </div>
              </b-col>
              <b-col md="4">
                <div
                  class="text-center p-3 border rounded"
                  :class="bulkResult.failureCount > 0 ? 'bg-danger text-white' : 'bg-light'"
                >
                  <div class="h3 font-weight-bold mb-0">{{ bulkResult.failureCount }}</div>
                  <div class="small">Failed</div>
                </div>
              </b-col>
            </b-row>

            <b-table
              :items="bulkResult.results"
              :fields="resultFields"
              bordered
              small
              responsive
              striped
            >
              <template #cell(status)="data">
                <b-badge :variant="data.item.success ? 'success' : 'danger'">
                  {{ data.item.success ? 'OK' : 'FAILED' }}
                </b-badge>
              </template>
              <template #cell(action)="data">
                <span v-if="data.item.success" class="text-capitalize">{{ data.item.action }}</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(unitsAdded)="data">
                <span v-if="data.item.success" class="text-success font-weight-bold">+{{ data.item.unitsAdded }}</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(newTotalUnits)="data">
                <span v-if="data.item.success">{{ data.item.newTotalUnits }} / {{ data.item.newRemainingUnits }}</span>
                <span v-else class="text-danger small">{{ data.item.error }}</span>
              </template>
            </b-table>
          </b-card>

        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "OrcidBulkAssignment",
  beforeRouteEnter(to, from, next) {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role === "Admin") {
      next();
    } else {
      next("/main/dashboard/sales");
    }
  },
  data() {
    return {
      orcidInput: "",
      units: null,
      transactionType: null,
      invoiceInfo: "",
      transactionTypeOptions: [
        { text: "Select Transaction Type", value: null, disabled: true },
        { text: "invoice", value: "invoice" },
        { text: "free_teaching", value: "free_teaching" },
      ],
      resultFields: [
        { key: "orcidId", label: "ORCID ID" },
        { key: "email", label: "Email" },
        { key: "status", label: "Status" },
        { key: "action", label: "Action" },
        { key: "unitsAdded", label: "Units Added" },
        { key: "newTotalUnits", label: "New Total / Remaining" },
      ],
      submitting: false,
      submitError: null,
      bulkResult: null,
    };
  },
  computed: {
    parsedOrcids() {
      return this.orcidInput
        .split(/[,\n\r]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    },
  },
  methods: {
    ...mapActions("usage", ["assignUnitsToOrcidBulk"]),

    resetForm() {
      this.orcidInput = "";
      this.units = null;
      this.transactionType = null;
      this.invoiceInfo = "";
      this.submitError = null;
      this.bulkResult = null;
    },

    async onSubmit() {
      this.submitError = null;
      this.bulkResult = null;
      this.submitting = true;

      try {
        const payload = {
          items: this.parsedOrcids.map((orcidId) => ({ orcidId, units: this.units })),
          transactionType: this.transactionType,
          invoiceInfo: this.invoiceInfo,
          bucketTypeId: 10,
          partnerId: "tair",
        };
        this.bulkResult = await this.assignUnitsToOrcidBulk(payload);
      } catch (err) {
        this.submitError = err.error || err.message || "An unexpected error occurred.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/usage/OrcidBulkAssignment.vue